
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import {
    TmcT,
    WriteOffFullItemT,
    WriteOffItemI,
    WriteOffT,
    WriteOffUpdateItemI,
    WriteoffUpdatePayloadI,
} from '@/core/types/tmc.types'
import { WarehouseT } from '@/core/types/warehouses.types'

interface ItemI {
    writeOffItemId?: number
    tmc_id: number
    tmc_name: string
    amount: number
}

// realisations
import { tmc } from '@/core/realisations/tmc.realisation'
import { warehouses } from '@/core/realisations/warehouses.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { required, integer, requiredIf } from '@vuelidate/validators'

// composable
import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'
import { useFormData } from '@/common/composable/useFormData'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-select': TheSelect,
        'the-input': TheInput,
        checkbox: Checkbox,
        'the-button': TheButton,
        'failed-fetch': FailedFetch,
        loader: Loader,
        FileReader,
    },
    setup() {
        const { findItemIndexById } = useHelpers()
        const notifications = useNotifications()

        const route = useRoute()
        const router = useRouter()
        const writeoff_id = Number(route.params.id as string)
        const { getFormData } = useFormData()

        const state = reactive({
            is_writeoff_loading: false,
            is_writeoff_failed: false,
            //
            is_loading: false,
            is_update: false,
            update_item_index: null as number | null,
            warehouse_id: null as number | null,
            invoice_number: null as string | null,
            //
            is_warehouse_consumption: true,
            is_invoice_number: false,
            is_project: false,
            //
            is_soft_package: false,
            is_grid: false,
            is_pallet: false,
            items: [] as ItemI[],
            is_item_removing: false,
            packageFiles: [] as File[],
            write_off: null as WriteOffT | null,
        })

        const isFiles = computed(() => {
            return state.is_invoice_number && (state.is_soft_package || state.is_grid)
        })
        const isFilesFront = computed(() => {
            return (
                isFiles.value &&
                state.write_off &&
                state.write_off.packageFiles &&
                state.write_off.packageFiles.length
            )
        })

        const mapWriteoffItems = (item: WriteOffFullItemT): ItemI => {
            return {
                writeOffItemId: item.id,
                tmc_id: item.inventoryItemId,
                tmc_name: item.inventoryItemName,
                amount: item.amount,
            }
        }

        const checkAdditionalServiceType = (additionalServiceType: number) => {
            if (additionalServiceType === 0) return

            if (additionalServiceType === 6) {
                state.is_grid = true
                return
            }

            if (additionalServiceType === 4) {
                state.is_soft_package = true
                return
            }

            if (additionalServiceType === 5) {
                state.is_pallet = true
                return
            }
        }

        const loadWriteoff = async () => {
            try {
                state.is_writeoff_loading = true

                const response = await tmc.getWriteOff(writeoff_id)

                state.warehouse_id = response.data.warehouseId
                state.invoice_number = response.data.invoiceNumber
                state.items = response.data.writeOffItems.map(mapWriteoffItems)

                state.is_invoice_number = response.data.writeOffTypeId === 1
                state.is_warehouse_consumption = response.data.writeOffTypeId === 2
                state.is_project = response.data.writeOffTypeId === 3

                state.write_off = response.data

                checkAdditionalServiceType(response.data.additionalServiceTypeId)

                state.is_writeoff_failed = false
            } catch (error) {
                console.error(error)
                state.is_writeoff_failed = true
            } finally {
                state.is_writeoff_loading = false
            }
        }

        const checkboxValidation = () => {
            return !(
                !state.is_warehouse_consumption &&
                !state.is_pallet &&
                !state.is_grid &&
                !state.is_soft_package
            )
        }

        const state_form$ = useVuelidate(
            {
                warehouse_id: { required },
                invoice_number: { required: requiredIf(() => !state.is_warehouse_consumption) },
                is_soft_package: { required: checkboxValidation },
                is_grid: { required: checkboxValidation },
                is_pallet: { required: checkboxValidation },
                packageFiles: {
                    required: requiredIf(
                        () => isFiles.value && !state.write_off?.packageFiles.length
                    ),
                },
            },
            state
        )

        const item_state = reactive({
            tmc_id: null as number | null,
            amount: null as number | null,
        })

        const item_form$ = useVuelidate(
            {
                tmc_id: { required },
                amount: { required, integer },
            },
            item_state
        )

        const data_state = reactive({
            tmc: new DataState<TmcT>({}),
            warehouses: new DataState<WarehouseT>({}),
        })

        const loadTMC = async (page: number, search?: string) => {
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true
                const response = await tmc.getList(page, search)
                data_state.tmc.list = response.data

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const loadMoreTMC = async (page: number) => {
            if (page > data_state.tmc.pagination.total_pages) return
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true

                const response = await tmc.getList(page)
                data_state.tmc.list = data_state.tmc.list.concat(response.data)

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const searchTMC = (search_query: string) => {
            loadTMC(1, search_query)
        }

        const unshiftFindedTMC = async (tmc: TmcT) => {
            await loadTMC(data_state.tmc.pagination.current_page)
            data_state.tmc.list.unshift(tmc)
        }

        const loadWarehouses = async () => {
            data_state.warehouses.pagination.current_page = 1

            try {
                data_state.warehouses.is_loading = true
                const response = await warehouses.getWarehouses()
                data_state.warehouses.list = response.data

                // data_state.warehouses.pagination.total_pages = response.meta.last_page

                data_state.warehouses.is_failed = false
                data_state.warehouses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.warehouses.failure.catchError(error)

                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_failed = true
                }
            } finally {
                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_loading = false
                }
            }
        }

        onBeforeMount(async () => {
            loadTMC(1)
            loadWarehouses()
            loadWriteoff()
        })

        const resetItemForm = () => {
            item_state.tmc_id = null
            item_state.amount = null
            item_form$.value.$reset()

            if (state.is_update) {
                state.is_update = false
                state.update_item_index = null
            }
        }

        const isTmcAlreadySelected = (tmc_id: number): boolean => {
            return state.items.some((item) => item.tmc_id === tmc_id)
        }

        const submitItem = () => {
            item_form$.value.$touch()
            if (item_form$.value.$invalid) return

            const tmc_id = item_state.tmc_id as number
            const amount = item_state.amount as number

            const index = findItemIndexById(tmc_id, data_state.tmc.list)
            if (index < 0) return

            const tmc_name = data_state.tmc.list[index].name

            const is_already_selected = isTmcAlreadySelected(tmc_id)

            if (is_already_selected) {
                notifications.notify({
                    title: `ТМЦ '${tmc_name}' уже выбран`,
                    description: 'Пожалуйста, выберите другой ТМЦ',
                    type: 'danger',
                })
                return
            }

            const item: ItemI = {
                tmc_id,
                tmc_name,
                amount,
            }

            state.items.push(item)

            resetItemForm()
        }

        const removeItem = async (item_index: number) => {
            if (state.is_item_removing) return

            const answer = confirm('Вы уверены что хотите удалить товар из списания?')
            if (!answer) return

            try {
                state.is_item_removing = true

                const item = state.items[item_index]

                if (item.writeOffItemId) {
                    await tmc.deleteWriteoffItem(item.writeOffItemId)
                }

                state.items.splice(item_index, 1)
                notifications.notify({ title: 'Товар удален из списания', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении товара из списания произошла ошибка',
                })
            } finally {
                state.is_item_removing = false
            }
        }

        const startUpdateItem = (item_index: number) => {
            state.update_item_index = item_index

            item_state.tmc_id = state.items[item_index].tmc_id
            item_state.amount = state.items[item_index].amount

            state.is_update = true
        }

        const updateItem = () => {
            item_form$.value.$touch()
            if (item_form$.value.$invalid) return

            const tmc_id = item_state.tmc_id as number
            const amount = item_state.amount as number

            const index = findItemIndexById(tmc_id, data_state.tmc.list)
            if (index < 0) return

            const tmc_name = data_state.tmc.list[index].name

            const is_already_selected = isTmcAlreadySelected(tmc_id)
            const maybe_selected_tmc_index = state.items.findIndex(
                (item) => item.tmc_id === item_state.tmc_id
            )

            if (is_already_selected && maybe_selected_tmc_index !== state.update_item_index) {
                notifications.notify({
                    title: `ТМЦ '${tmc_name}' уже выбран`,
                    description: 'Пожалуйста, выберите другой ТМЦ',
                    type: 'danger',
                })
                return
            }

            state.items[state.update_item_index as number].tmc_id = tmc_id
            state.items[state.update_item_index as number].tmc_name = tmc_name
            state.items[state.update_item_index as number].amount = amount

            resetItemForm()
        }

        const mapItems = (item: ItemI): WriteOffUpdateItemI | WriteOffItemI => {
            if (item.writeOffItemId) {
                return {
                    writeOffItemId: item.writeOffItemId,
                    inventoryItemId: item.tmc_id,
                    amount: Number(item.amount),
                }
            }

            return {
                inventoryItemId: item.tmc_id,
                amount: Number(item.amount),
            }
        }

        const getAdditionalServiceTypeId = (): number | null => {
            if (state.is_grid) return 6
            if (state.is_soft_package) return 4
            if (state.is_pallet) return 5

            return null
        }

        const submit = async () => {
            if (state.is_loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (item_state.tmc_id || item_state.amount) {
                if (state.is_update) {
                    updateItem()
                } else {
                    submitItem()
                }

                if (item_state.tmc_id || item_state.amount) return
            }

            if (state.items.length === 0) {
                return
            }

            try {
                state.is_loading = true

                const items = state.items.map(mapItems)

                const payload: WriteoffUpdatePayloadI = {
                    warehouseId: state.warehouse_id as number,
                    invoiceNumber: state.invoice_number,
                    writeOffTypeId: state.is_warehouse_consumption ? 2 : 1,
                    additionalServiceTypeId: getAdditionalServiceTypeId(),
                    writeOffItems: items,
                }

                if (state.packageFiles.length) {
                    payload.packageFiles = state.packageFiles
                }

                await tmc.updateWriteOff(writeoff_id, getFormData(payload))

                notifications.notify({ title: 'Списание сохранено', type: 'success' })
                router.go(-1)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении списания произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const resetAfterCheckboxChange = () => {
            state.warehouse_id = null
            state.invoice_number = null

            state.is_soft_package = false
            state.is_grid = false
            state.is_pallet = false

            state_form$.value.$reset()
            resetItemForm()
        }

        const isPDF = (url: string) => {
            let extension_tmp = url.split('.')
            return extension_tmp[extension_tmp.length - 1] === 'pdf'
        }

        const downloadFile = async (download_url: string) => {
            const link = document.createElement('a')
            link.href = download_url
            link.target = '_blank'
            document.body.appendChild(link)
            link.click()
            link.remove()
        }

        const onWarehouseChange = (warehouse_type: number) => {
            resetAfterCheckboxChange()

            switch (warehouse_type) {
                case 1:
                    state.is_warehouse_consumption = false
                    state.is_project = false
                    break
                case 2:
                    state.is_invoice_number = false
                    state.is_project = false
                    break
                case 3:
                    state.is_invoice_number = false
                    state.is_warehouse_consumption = false
                    break
                default:
                    break
            }
        }

        const deleteFile = async (id: number) => {
            const answer = confirm('Вы действительно хотите удалить файл?')
            if (!answer) return

            try {
                state.is_loading = true

                await tmc.deletePackageFile(id)
                await loadWriteoff()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении файлы произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        return {
            state,
            state_form$,
            item_state,
            item_form$,
            data_state,
            isFilesFront,
            //
            loadTMC,
            loadMoreTMC,
            searchTMC,
            unshiftFindedTMC,
            loadWarehouses,
            //
            submitItem,
            removeItem,
            //
            startUpdateItem,
            updateItem,
            //
            submit,
            //
            loadWriteoff,
            writeoff_id,
            onWarehouseChange,
            isFiles,
            isPDF,
            downloadFile,
            deleteFile,
        }
    },
})
